import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { auth, db } from '../firebase.config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { authService } from '../services/authService';

const AuthContext = createContext();

const initialState = {
  currentUser: null,
  loading: true,
  orgData: null
};

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        currentUser: action.payload.user,
        orgData: {
          org_id: action.payload.user.org_id,
          role: action.payload.user.role,
          permissions: action.payload.user.permissions,
          status: action.payload.user.status
        },
        loading: false
      };
    case 'LOGOUT':
      return {
        ...state,
        currentUser: null,
        orgData: null,
        loading: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    default:
      return state;
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        dispatch({ type: 'LOGOUT' });
        return;
      }
      
      try {
        // Validate user against org_users collection
        const email = user.email;
        
        // Use the validateOrgUser function from authService
        try {
          const orgUser = await authService.validateOrgUser(email);
          
          // User is valid, update state with org data
          dispatch({
            type: 'LOGIN',
            payload: {
              user: {
                ...user,
                org_id: orgUser.org_id,
                role: orgUser.role,
                permissions: orgUser.permissions,
                status: orgUser.status
              }
            }
          });
        } catch (validationError) {
          // User is authenticated but not authorized or not active
          console.error('User validation error:', validationError.message);
          
          // Sign the user out since they're not authorized
          await signOut(auth);
          
          dispatch({ type: 'LOGOUT' });
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        dispatch({ type: 'LOGOUT' });
      }
    });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{ 
      currentUser: state.currentUser, 
      orgData: state.orgData,
      loading: state.loading,
      theme,
      setTheme,
      dispatch 
    }}>
      {!state.loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export { AuthContext };
